import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAdslotsQuery } from 'services/api/creativeServer';
import { Button, ThemeProvider } from '@alphonsocode/kallos';
import * as S from '../../../PodHomePage/components/Screen/components/OnDemandModal/style';
import {
  ModalBodyProps,
  Option,
  AssetType,
  AssetDimensions,
} from '../../../PodHomePage/components/Screen/components/OnDemandModal/types';
import { useFileUpload } from '../../../PodHomePage/components/Screen/components/OnDemandModal/hooks/useFileUpload';
import { useAppSearch } from '../../../PodHomePage/components/Screen/components/OnDemandModal/hooks/useAppSearch';
import { useContentPartnerSearch } from '../../../PodHomePage/components/Screen/components/OnDemandModal/hooks/useContentPartnerSearch';
import { useMosaicValidation } from '../../../PodHomePage/components/Screen/components/OnDemandModal/hooks/useMosaicValidation';
import { useStreamMetadata } from '../../../PodHomePage/components/Screen/components/OnDemandModal/hooks/useStreamMetadata';
import { useFormValidation } from './hooks/useFormValidation';
import {
  getAdsureGeneratedTestCount,
  getMaxBatchTestsMessage,
  MAX_BATCH_TESTS,
  useSaveWorkflow,
} from './hooks/useSaveWorkflow';
import { ManualUploadFormAdsure } from './components/ManualUploadFormAdsure';
import { useAdslotValidation } from './hooks/useAdslotValidator';
import { MosaicForm } from './components/MosaicForm';
import { useOSVersionSearch } from './hooks/useOSVersionSearch';
import { ProgressView } from './components/ProgressView';
import { generateDummyBanner } from 'utils/helpers';
import { Tabs } from './components/Tabs';
import { WebTesterForm } from './components/WebTesterForm';
import type { AdsureBatchItem } from './hooks/useSaveWorkflow';

const defaultContentPartners: Option[] = [
  {
    key: 'weboshomeconnect',
    value: 'weboshomeconnect',
    label: 'weboshomeconnect',
  },
  { key: 'lgchannels', value: 'lgchannels', label: 'lgchannels' },
];

const CLICKABLE_VIDEO_OPTION: Option = {
  key: 'clickableVideoTo',
  value: 'clickableVideoTo',
  label: 'clickableVideoTo',
};

const MAX_IMAGE_SIZE = 3 * 1024 * 1024;
const MAX_VIDEO_SIZE = 30 * 1024 * 1024;
const MAX_FILES_PER_UPLOAD = 10;

const formatInput = (value: string): Option => ({
  key: value,
  value,
  label: value,
});

const readFilePreview = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => resolve(e.target?.result as string);
    reader.onerror = () => reject(new Error('Could not read asset file'));
    reader.readAsDataURL(file);
  });

const getImageDimensions = (preview: string): Promise<AssetDimensions> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = () => reject(new Error('Could not read image dimensions'));
    img.src = preview;
  });

const getVideoDimensions = (preview: string): Promise<AssetDimensions> =>
  new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () =>
      resolve({
        width: video.videoWidth,
        height: video.videoHeight,
        duration: parseFloat(video.duration.toFixed(2)),
      });
    video.onerror = () => reject(new Error('Could not read video metadata'));
    video.src = preview;
  });

const getAssetSizeError = (file: File) => {
  const isImage = file.type.startsWith('image/');
  const isVideo = file.type.startsWith('video/');
  const limit = isImage ? MAX_IMAGE_SIZE : MAX_VIDEO_SIZE;

  if (!isImage && !isVideo) return 'Unsupported asset type.';
  if (file.size <= limit) return '';

  return `${isImage ? 'Image' : 'Video'} exceeds size limit of ${
    isImage ? '3MB' : '30MB'
  }.`;
};

const getAssetMetadata = async (file: File) => {
  const preview = await readFilePreview(file);
  if (file.type.startsWith('image/')) {
    return {
      preview,
      dimensions: await getImageDimensions(preview),
    };
  }
  if (file.type.startsWith('video/')) {
    return {
      preview,
      dimensions: await getVideoDimensions(preview),
    };
  }

  return { preview, dimensions: null };
};

export default function AdsureModalBody({ setOnDemandModal }: ModalBodyProps) {
  const hasSentRef = useRef(false);

  const [activeTab, setActiveTab] = useState<'manual' | 'mosaic' | 'webTester'>(
    'manual',
  );
  const [isOpen, setIsOpen] = useState(false);
  const [contentTargetValue, setContentTargetValue] = useState('');
  const [destinationURL, setDestinationURL] = useState('');
  const [selectedAdType, setSelectedAdType] = useState<Option | null>(null);
  const [selectedDestinationType, setDestinationType] = useState<Option | null>(
    null,
  );
  const [selectedAppId, setselectedAppId] = useState<Option | null>(null);
  const [selectedAdslot, setSelectedAdslot] = useState<Option | null>(null);
  const [selectedAdslots, setSelectedAdslots] = useState<Option[]>([]);
  const [selectedContentPartner, setContentPartner] = useState<Option[]>(
    defaultContentPartners,
  );
  const [selectedCountry, setCountry] = useState<Option | null>(null);

  const [selectedOSV, setOsv] = useState<Option[] | null>(null);
  const [os, setOs] = useState('');

  // Tracks whether primary+secondary were auto-filled from a video upload
  const [isVideoAutoFilled, setIsVideoAutoFilled] = useState(false);
  const [batchItems, setBatchItems] = useState<AdsureBatchItem[]>([]);
  const [selectedBatchItemId, setSelectedBatchItemId] = useState<string | null>(
    null,
  );
  const [primaryAssetError, setPrimaryAssetError] = useState('');
  const [uploadActionError, setUploadActionError] = useState('');
  const isHydratingBatchItemRef = useRef(false);

  useEffect(() => {
    if (!selectedOSV || selectedOSV.length === 0) {
      setOs('');
      return;
    }

    setOs(selectedOSV.map(o => o.value).join(','));
  }, [selectedOSV]);

  // File upload hooks
  const primaryAssetUpload = useFileUpload();
  const secondaryAssetUpload = useFileUpload();
  const destinationAssetUpload = useFileUpload();
  const clickableStream = useStreamMetadata();
  const optionalStream = useStreamMetadata();
  const destinationStream = useStreamMetadata();

  // Search hooks
  const appSearch = useAppSearch({ country: selectedCountry?.label ?? '', os });
  const contentPartnerSearch = useContentPartnerSearch();
  const osVersionSearch = useOSVersionSearch(selectedAdslot?.label || '');
  // Mosaic validation hook
  const mosaicValidation = useMosaicValidation({
    country: selectedCountry?.label ?? null,
    os: '',
  });

  // Adslots query
  const {
    data: adslotsData,
    isFetching: isAdslotFetching,
    isLoading: isAdslotLoading,
    refetch: refetchAdslots,
  } = useAdslotsQuery(
    {
      height: primaryAssetUpload.dimensions?.height || 0,
      width: primaryAssetUpload.dimensions?.width || 0,
    },
    {
      skip:
        !primaryAssetUpload.dimensions?.height ||
        !primaryAssetUpload.dimensions?.width,
    },
  );
  const adslotValidation = useAdslotValidation({
    height: primaryAssetUpload.dimensions?.height,
    width: primaryAssetUpload.dimensions?.width,
  });
  // Form validation
  const validation = useFormValidation({
    activeTab,
    primaryAsset: primaryAssetUpload.file,
    primaryAssetError,
    secondaryAsset: secondaryAssetUpload.file,
    destinationAsset: destinationAssetUpload.file,
    selectedAdType,
    selectedDestinationType,
    selectedAppId,
    contentTargetValue,
    destinationURL,
    selectedAdslot,
    selectedAdslots,
    selectedContentPartner,
    campaignID: mosaicValidation.campaignID,
    creativeID: mosaicValidation.creativeID,
    adslotState: mosaicValidation.adslotState,
    creativeAdslots: mosaicValidation.creativeAdslots,
    validationStatus: adslotValidation.validationStatus,
    validateCheck: adslotValidation.validateCheck,
    selectedCountry,
    osv: selectedOSV,
    mandatoryFields: adslotValidation.mandatoryFields,
    setMandatoryFields: adslotValidation.setMandatoryFields,
    clickableStreamUrl: clickableStream.streamUrl,
    clickableStreamMetadata: clickableStream.metadata,
    clickableStreamError: clickableStream.error,
    optionalStreamUrl: optionalStream.streamUrl,
    optionalStreamMetadata: optionalStream.metadata,
    optionalStreamError: optionalStream.error,
    destinationStreamUrl: destinationStream.streamUrl,
    destinationStreamMetadata: destinationStream.metadata,
    destinationStreamError: destinationStream.error,
    batchItemCount: batchItems.length,
  });

  // Save workflow
  const saveWorkflow = useSaveWorkflow({
    os,
    country: selectedCountry?.label ?? '',
    primaryAsset: primaryAssetUpload.file,
    secondaryAsset: secondaryAssetUpload.file,
    destinationAsset: destinationAssetUpload.file,
    selectedAdType,
    selectedDestinationType,
    selectedAppId,
    contentTargetValue,
    destinationURL,
    selectedAdslot,
    selectedAdslots,
    selectedContentPartner,
    campaignID: mosaicValidation.campaignID,
    creativeID: mosaicValidation.creativeID,
    setOnDemandModal,
    activeTab,
    batchItems,
    clickableStreamMetadata: clickableStream.metadata,
    optionalStreamMetadata: optionalStream.metadata,
    destinationStreamMetadata: destinationStream.metadata,
  });

  // Refetch adslots when primary dimensions change
  useEffect(() => {
    if (
      primaryAssetUpload.dimensions?.height &&
      primaryAssetUpload.dimensions?.width
    ) {
      refetchAdslots();
    }
  }, [primaryAssetUpload.dimensions, refetchAdslots]);

  useEffect(() => {
    if (isHydratingBatchItemRef.current) return;
    if (!selectedBatchItemId && !isHydratingBatchItemRef.current) {
      setSelectedAdslot(null);
      setSelectedAdslots([]);
    }
    if (
      primaryAssetUpload.file !== null &&
      primaryAssetUpload.dimensions?.height &&
      primaryAssetUpload.dimensions?.width
    )
      adslotValidation.handleValidateChange();
  }, [
    primaryAssetUpload.file,
    primaryAssetUpload.dimensions?.height,
    primaryAssetUpload.dimensions?.width,
  ]);

  const applyDefaultConfigForAdslots = useCallback(
    (adslots: Option[]) => {
      const slotNames = adslots
        .map(adslot => adslot.value || adslot.label)
        .filter(Boolean);
      if (!slotNames.length) return;

      const getCommonValues = (field: string) => {
        const optionSets = slotNames.map(
          slotName => adslotValidation?.validateCheck?.[slotName]?.[field] || [],
        );
        const [firstSet = []] = optionSets;
        return firstSet.filter((item: string) =>
          optionSets.every(options => options.includes(item)),
        );
      };

      const adTypeDefaults = getCommonValues('adType');
      const destinationTypeDefaults = getCommonValues('destinationType');
      const countryDefaults = getCommonValues('countryCode');
      const osvDefaults = getCommonValues('osvs');

      if (!isVideoAutoFilled && adTypeDefaults[0]) {
        setSelectedAdType(formatInput(adTypeDefaults[0]));
      }
      setDestinationType(
        destinationTypeDefaults[0]
          ? formatInput(destinationTypeDefaults[0])
          : null,
      );
      setCountry(countryDefaults[0] ? formatInput(countryDefaults[0]) : null);
      setOsv(osvDefaults[0] ? [formatInput(osvDefaults[0])] : null);
    },
    [adslotValidation?.validateCheck, isVideoAutoFilled],
  );

  useEffect(() => {
    const slotNames = adslotValidation?.adslots;
    if (!slotNames || slotNames.length === 0) return;
    if (selectedBatchItemId && selectedAdslots.length > 0) return;

    const firstSlotName = slotNames[0];
    const slotObj = adslotValidation?.validateCheck?.[firstSlotName];

    if (!slotObj) return;

    setSelectedAdslot(formatInput(firstSlotName));
    setSelectedAdslots([formatInput(firstSlotName)]);
    applyDefaultConfigForAdslots([formatInput(firstSlotName)]);
  }, [adslotValidation?.adslots, applyDefaultConfigForAdslots]);

  // ─── Video upload helpers ──────────────────────────────────────────────────

  const extractVideoDimensions = (
    videoFile: File,
  ): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(videoFile);
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        resolve({ width: video.videoWidth, height: video.videoHeight });
      };
      video.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Could not read video metadata'));
      };
      video.src = url;
    });
  };

  const handleVideoAsPrimary = useCallback(
    async (videoFile: File) => {
      try {
        setPrimaryAssetError(getAssetSizeError(videoFile));
        const { width, height } = await extractVideoDimensions(videoFile);
        const dummyFile = await generateDummyBanner(width, height);

        primaryAssetUpload.handleFile(dummyFile, 'primary');
        secondaryAssetUpload.handleFile(videoFile, 'secondary');
        setSelectedAdType(CLICKABLE_VIDEO_OPTION);
        setIsVideoAutoFilled(true);
      } catch (err) {
        console.error('Video primary upload failed:', err);
        alert('Failed to process video. Please try again.');
      }
    },
    [primaryAssetUpload, secondaryAssetUpload],
  );

  // ─── Primary asset handlers (intercept video) ─────────────────────────────

  const getCurrentValidationMessage = useCallback(() => {
    if (primaryAssetError) return primaryAssetError;
    if (adslotValidation.validateError) return adslotValidation.validateError;
    if (validation.mandatoryFields) return validation.mandatoryFields;
    return validation.saveStatus ? '' : 'Invalid test: please correct configuration.';
  }, [
    adslotValidation.validateError,
    primaryAssetError,
    validation.mandatoryFields,
    validation.saveStatus,
  ]);

  const loadBatchItemIntoForm = useCallback(
    (item: AdsureBatchItem) => {
      isHydratingBatchItemRef.current = true;
      const hasStoredAdslotValidation =
        item.adslotValidationStatus ||
        item.adslotValidateError ||
        (item.adslotValidateCheck &&
          Object.keys(item.adslotValidateCheck).length > 0);
      adslotValidation.setValidationState({
        validateCheck: item.adslotValidateCheck,
        validationStatus: item.adslotValidationStatus,
        validateError: item.adslotValidateError,
        mandatoryFields: item.adslotMandatoryFields,
      });
      setUploadActionError('');
      setSelectedBatchItemId(item.clientId);
      primaryAssetUpload.setUploadState(
        item.primaryAsset,
        item.primaryPreview || null,
        item.primaryDimensions,
      );
      secondaryAssetUpload.setUploadState(
        item.secondaryAsset,
        item.secondaryPreview || null,
        item.secondaryDimensions,
      );
      destinationAssetUpload.setUploadState(
        item.destinationAsset,
        item.destinationPreview || null,
        item.destinationDimensions,
      );
      setSelectedAdType(item.selectedAdType);
      setDestinationType(item.selectedDestinationType);
      setselectedAppId(item.selectedAppId);
      setContentTargetValue(item.contentTargetValue);
      setDestinationURL(item.destinationURL);
      setSelectedAdslots(item.selectedAdslots);
      setSelectedAdslot(item.selectedAdslots[0] || null);
      setContentPartner(item.selectedContentPartner);
      setCountry(item.country);
      setOsv(item.osv);
      setPrimaryAssetError(item.primaryAssetError || '');
      setIsVideoAutoFilled(Boolean(item.isVideoAutoFilled));
      clickableStream.setStreamState(
        item.clickableStreamUrl || item.clickableStreamMetadata?.url || '',
        item.clickableStreamMetadata,
        item.clickableStreamError || '',
      );
      optionalStream.setStreamState(
        item.optionalStreamUrl || item.optionalStreamMetadata?.url || '',
        item.optionalStreamMetadata,
        item.optionalStreamError || '',
      );
      destinationStream.setStreamState(
        item.destinationStreamUrl || item.destinationStreamMetadata?.url || '',
        item.destinationStreamMetadata,
        item.destinationStreamError || '',
      );

      window.setTimeout(() => {
        isHydratingBatchItemRef.current = false;
        if (!hasStoredAdslotValidation && item.primaryDimensions) {
          adslotValidation.handleValidateChange({
            height: item.primaryDimensions.height,
            width: item.primaryDimensions.width,
          });
        }
      }, 0);
    },
    [
      clickableStream,
      destinationAssetUpload,
      destinationStream,
      optionalStream,
      primaryAssetUpload,
      secondaryAssetUpload,
      adslotValidation,
    ],
  );

  const createBatchItemFromPrimaryFile = useCallback(
    async (file: File, index: number): Promise<AdsureBatchItem> => {
      const sizeError = getAssetSizeError(file);
      const clientId = `asset-${Date.now()}-${index}`;
      const defaultInvalidMessage =
        sizeError || 'Invalid test: please correct configuration.';

      try {
        if (file.type.startsWith('video/')) {
          const secondaryMetadata = await getAssetMetadata(file);
          const primaryBanner = await generateDummyBanner(
            secondaryMetadata.dimensions?.width || 0,
            secondaryMetadata.dimensions?.height || 0,
          );
          const primaryMetadata = await getAssetMetadata(primaryBanner);

          return {
            clientId,
            label: file.name,
            primaryAsset: primaryBanner,
            primaryPreview: primaryMetadata.preview,
            primaryDimensions: primaryMetadata.dimensions,
            primaryAssetError: sizeError,
            secondaryAsset: file,
            secondaryPreview: secondaryMetadata.preview,
            secondaryDimensions: secondaryMetadata.dimensions,
            destinationAsset: null,
            destinationPreview: null,
            destinationDimensions: null,
            selectedAdType: CLICKABLE_VIDEO_OPTION,
            selectedDestinationType: null,
            selectedAppId: null,
            contentTargetValue: '',
            destinationURL: '',
            selectedAdslots: [],
            selectedContentPartner: defaultContentPartners,
            country: null,
            osv: [],
            isValid: false,
            validationMessage: defaultInvalidMessage,
            isVideoAutoFilled: true,
            adslotValidateCheck: {},
            adslotValidationStatus: false,
            adslotValidateError: '',
            adslotMandatoryFields: '',
            clickableStreamUrl: '',
            clickableStreamError: '',
            optionalStreamUrl: '',
            optionalStreamError: '',
            destinationStreamUrl: '',
            destinationStreamError: '',
            clickableStreamMetadata: null,
            optionalStreamMetadata: null,
            destinationStreamMetadata: null,
          };
        }

        const primaryMetadata = await getAssetMetadata(file);
        return {
          clientId,
          label: file.name,
          primaryAsset: file,
          primaryPreview: primaryMetadata.preview,
          primaryDimensions: primaryMetadata.dimensions,
          primaryAssetError: sizeError,
          secondaryAsset: null,
          secondaryPreview: null,
          secondaryDimensions: null,
          destinationAsset: null,
          destinationPreview: null,
          destinationDimensions: null,
          selectedAdType: null,
          selectedDestinationType: null,
          selectedAppId: null,
          contentTargetValue: '',
          destinationURL: '',
          selectedAdslots: [],
          selectedContentPartner: defaultContentPartners,
          country: null,
          osv: [],
          isValid: false,
          validationMessage: defaultInvalidMessage,
          isVideoAutoFilled: false,
          adslotValidateCheck: {},
          adslotValidationStatus: false,
          adslotValidateError: '',
          adslotMandatoryFields: '',
          clickableStreamUrl: '',
          clickableStreamError: '',
          optionalStreamUrl: '',
          optionalStreamError: '',
          destinationStreamUrl: '',
          destinationStreamError: '',
          clickableStreamMetadata: null,
          optionalStreamMetadata: null,
          destinationStreamMetadata: null,
        };
      } catch {
        const readError =
          sizeError || 'Could not read asset metadata. Please choose another file.';
        return {
          clientId,
          label: file.name,
          primaryAsset: file,
          primaryPreview: null,
          primaryDimensions: null,
          primaryAssetError: readError,
          secondaryAsset: null,
          secondaryPreview: null,
          secondaryDimensions: null,
          destinationAsset: null,
          destinationPreview: null,
          destinationDimensions: null,
          selectedAdType: null,
          selectedDestinationType: null,
          selectedAppId: null,
          contentTargetValue: '',
          destinationURL: '',
          selectedAdslots: [],
          selectedContentPartner: defaultContentPartners,
          country: null,
          osv: [],
          isValid: false,
          validationMessage: readError,
          isVideoAutoFilled: false,
          adslotValidateCheck: {},
          adslotValidationStatus: false,
          adslotValidateError: '',
          adslotMandatoryFields: '',
          clickableStreamUrl: '',
          clickableStreamError: '',
          optionalStreamUrl: '',
          optionalStreamError: '',
          destinationStreamUrl: '',
          destinationStreamError: '',
          clickableStreamMetadata: null,
          optionalStreamMetadata: null,
          destinationStreamMetadata: null,
        };
      }
    },
    [],
  );

  const addPrimaryFilesToBatch = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      const nextStartIndex = batchItems.length + 1;
      const nextItems = await Promise.all(
        files.map((file, index) =>
          createBatchItemFromPrimaryFile(file, nextStartIndex + index),
        ),
      );

      setBatchItems(prev => [...prev, ...nextItems]);
      loadBatchItemIntoForm(nextItems[0]);
    },
    [
      batchItems.length,
      createBatchItemFromPrimaryFile,
      loadBatchItemIntoForm,
    ],
  );

  const getUploadLimitError = (fileCount: number) =>
    fileCount > MAX_FILES_PER_UPLOAD
      ? `You can upload up to ${MAX_FILES_PER_UPLOAD} creative files at a time. Please select ${MAX_FILES_PER_UPLOAD} or fewer files, or upload the remaining creatives in another upload.`
      : '';

  const handlePrimaryFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, type: AssetType) => {
      const uploadedFiles = Array.from(e.target.files || []);
      if (uploadedFiles.length === 0) return;

      const limitError = getUploadLimitError(uploadedFiles.length);
      if (limitError) {
        setUploadActionError(limitError);
        e.target.value = '';
        return;
      }
      setUploadActionError('');

      if (
        uploadedFiles.length > 1 ||
        batchItems.length > 0 ||
        selectedBatchItemId
      ) {
        addPrimaryFilesToBatch(uploadedFiles);
        e.target.value = '';
        return;
      }

      const uploadedFile = uploadedFiles[0];
      if (uploadedFile.type.startsWith('video/')) {
        handleVideoAsPrimary(uploadedFile);
      } else {
        setIsVideoAutoFilled(false);
        setPrimaryAssetError(getAssetSizeError(uploadedFile));
        primaryAssetUpload.handleFileUpload(e, type);
      }
    },
    [
      addPrimaryFilesToBatch,
      batchItems.length,
      handleVideoAsPrimary,
      primaryAssetUpload,
      selectedBatchItemId,
    ],
  );

  const handlePrimaryDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>, type: AssetType) => {
      e.preventDefault();
      const uploadedFiles = Array.from(e.dataTransfer.files || []);
      if (uploadedFiles.length === 0) return;

      const limitError = getUploadLimitError(uploadedFiles.length);
      if (limitError) {
        setUploadActionError(limitError);
        return;
      }
      setUploadActionError('');

      if (
        uploadedFiles.length > 1 ||
        batchItems.length > 0 ||
        selectedBatchItemId
      ) {
        addPrimaryFilesToBatch(uploadedFiles);
        return;
      }

      const uploadedFile = uploadedFiles[0];
      if (uploadedFile.type.startsWith('video/')) {
        handleVideoAsPrimary(uploadedFile);
      } else {
        setIsVideoAutoFilled(false);
        setPrimaryAssetError(getAssetSizeError(uploadedFile));
        primaryAssetUpload.handleDrop(e, type);
      }
    },
    [
      addPrimaryFilesToBatch,
      batchItems.length,
      handleVideoAsPrimary,
      primaryAssetUpload,
      selectedBatchItemId,
    ],
  );

  // ─── Primary remove — resets everything including country/osv ─────────────

  const handlePrimaryRemove = useCallback(() => {
    if (selectedBatchItemId) {
      setBatchItems(prev =>
        prev.filter(item => item.clientId !== selectedBatchItemId),
      );
      setSelectedBatchItemId(null);
    }
    primaryAssetUpload.handleRemove();
    setPrimaryAssetError('');
    setUploadActionError('');
    setSelectedAdType(null);
    setDestinationType(null);
    setCountry(null);
    setOsv(null);
    setselectedAppId(null);
    setContentTargetValue('');
    setDestinationURL('');
    setSelectedAdslot(null);
    setSelectedAdslots([]);
    secondaryAssetUpload.reset();
    destinationAssetUpload.reset();
    clickableStream.reset();
    optionalStream.reset();
    destinationStream.reset();
    adslotValidation.reset();
    setIsVideoAutoFilled(false);
  }, [
    selectedBatchItemId,
    primaryAssetUpload,
    secondaryAssetUpload,
    destinationAssetUpload,
    clickableStream,
    optionalStream,
    destinationStream,
    adslotValidation,
  ]);

  // ─── Dropdown change handlers ──────────────────────────────────────────────

  const handleAdTypeChange = (value: Option | null) => {
    setSelectedAdType(value);
    if (isVideoAutoFilled) {
      setIsVideoAutoFilled(false);
      secondaryAssetUpload.reset();
    } else {
      secondaryAssetUpload.reset();
    }
  };

  const handleAdslotsChange = (value: Option[]) => {
    const nextAdslots = value || [];
    setSelectedAdslots(nextAdslots);
    setSelectedAdslot(nextAdslots?.[0] || null);
    setSelectedAdType(null);
    setDestinationType(null);
    setCountry(null);
    setOsv(null);
    setselectedAppId(null);
    setContentTargetValue('');
    setDestinationURL('');
    secondaryAssetUpload.reset();
    destinationAssetUpload.reset();
    clickableStream.reset();
    optionalStream.reset();
    destinationStream.reset();
    setIsVideoAutoFilled(false);
    if (nextAdslots.length > 0) {
      applyDefaultConfigForAdslots(nextAdslots);
    }
  };

  const handleAdslotChange = (value: Option | null) => {
    handleAdslotsChange(value ? [value] : []);
  };

  const handleDestinationTypeChange = (value: Option | null) => {
    setDestinationType(value);
    setselectedAppId(null);
    setContentTargetValue('');
    setDestinationURL('');
    destinationAssetUpload.reset();
    destinationStream.reset();
  };

  const resetCurrentManualConfig = useCallback(() => {
    setSelectedBatchItemId(null);
    setPrimaryAssetError('');
    setUploadActionError('');
    primaryAssetUpload.handleRemove();
    secondaryAssetUpload.reset();
    destinationAssetUpload.reset();
    setSelectedAdType(null);
    setDestinationType(null);
    setCountry(null);
    setOsv(null);
    setselectedAppId(null);
    setContentTargetValue('');
    setDestinationURL('');
    setSelectedAdslot(null);
    setSelectedAdslots([]);
    adslotValidation.reset();
    clickableStream.reset();
    optionalStream.reset();
    destinationStream.reset();
    setIsVideoAutoFilled(false);
  }, [
    adslotValidation,
    clickableStream,
    destinationStream,
    optionalStream,
    destinationAssetUpload,
    primaryAssetUpload,
    secondaryAssetUpload,
  ]);

  const handleAddCurrentToBatch = useCallback(() => {
    if (
      !primaryAssetUpload.file ||
      !selectedAdType ||
      !selectedDestinationType ||
      !selectedCountry ||
      !selectedOSV ||
      selectedOSV.length === 0 ||
      selectedAdslots.length === 0 ||
      primaryAssetError
    ) {
      return;
    }

    const nextIndex = batchItems.length + 1;
    setBatchItems(prev => [
      ...prev,
      {
        clientId: `asset-${Date.now()}-${nextIndex}`,
        label: primaryAssetUpload.file?.name || `Creative ${nextIndex}`,
        primaryAsset: primaryAssetUpload.file!,
        primaryPreview: primaryAssetUpload.preview,
        primaryDimensions: primaryAssetUpload.dimensions,
        primaryAssetError: '',
        secondaryAsset: secondaryAssetUpload.file,
        secondaryPreview: secondaryAssetUpload.preview,
        secondaryDimensions: secondaryAssetUpload.dimensions,
        destinationAsset: destinationAssetUpload.file,
        destinationPreview: destinationAssetUpload.preview,
        destinationDimensions: destinationAssetUpload.dimensions,
        selectedAdType,
        selectedDestinationType,
        selectedAppId,
        contentTargetValue,
        destinationURL,
        selectedAdslots,
        selectedContentPartner,
        country: selectedCountry,
        osv: selectedOSV,
        isValid: true,
        validationMessage: '',
        isVideoAutoFilled,
        adslotValidateCheck: adslotValidation.validateCheck,
        adslotValidationStatus: adslotValidation.validationStatus,
        adslotValidateError: adslotValidation.validateError,
        adslotMandatoryFields: adslotValidation.mandatoryFields,
        clickableStreamUrl: clickableStream.streamUrl,
        clickableStreamError: clickableStream.error,
        optionalStreamUrl: optionalStream.streamUrl,
        optionalStreamError: optionalStream.error,
        destinationStreamUrl: destinationStream.streamUrl,
        destinationStreamError: destinationStream.error,
        clickableStreamMetadata: clickableStream.metadata,
        optionalStreamMetadata: optionalStream.metadata,
        destinationStreamMetadata: destinationStream.metadata,
      },
    ]);
    resetCurrentManualConfig();
  }, [
    batchItems.length,
    adslotValidation.mandatoryFields,
    adslotValidation.validateCheck,
    adslotValidation.validateError,
    adslotValidation.validationStatus,
    clickableStream.error,
    clickableStream.metadata,
    clickableStream.streamUrl,
    contentTargetValue,
    destinationAssetUpload.dimensions,
    destinationAssetUpload.file,
    destinationAssetUpload.preview,
    destinationStream.error,
    destinationStream.metadata,
    destinationStream.streamUrl,
    destinationURL,
    optionalStream.error,
    optionalStream.metadata,
    optionalStream.streamUrl,
    primaryAssetUpload.dimensions,
    primaryAssetUpload.file,
    primaryAssetUpload.preview,
    primaryAssetError,
    resetCurrentManualConfig,
    secondaryAssetUpload.dimensions,
    secondaryAssetUpload.file,
    secondaryAssetUpload.preview,
    isVideoAutoFilled,
    selectedAdType,
    selectedAdslots,
    selectedAppId,
    selectedContentPartner,
    selectedCountry,
    selectedDestinationType,
    selectedOSV,
  ]);

  const handleSelectBatchItem = useCallback(
    (clientId: string) => {
      const item = batchItems.find(batchItem => batchItem.clientId === clientId);
      if (item) loadBatchItemIntoForm(item);
    },
    [batchItems, loadBatchItemIntoForm],
  );

  const handleRemoveBatchItem = useCallback(
    (clientId: string) => {
      setBatchItems(prev => prev.filter(item => item.clientId !== clientId));
      if (selectedBatchItemId === clientId) {
        resetCurrentManualConfig();
      }
    },
    [resetCurrentManualConfig, selectedBatchItemId],
  );

  const handleAddMoreCreatives = useCallback(() => {
    resetCurrentManualConfig();
  }, [resetCurrentManualConfig]);

  useEffect(() => {
    if (!selectedBatchItemId || isHydratingBatchItemRef.current) return;
    if (!primaryAssetUpload.file) return;

    const validationMessage = getCurrentValidationMessage();
    const isCurrentItemValid =
      validation.saveStatus &&
      !primaryAssetError &&
      !adslotValidation.validateError;

    setBatchItems(prev =>
      prev.map(item =>
        item.clientId === selectedBatchItemId
          ? {
              ...item,
              label: item.label || primaryAssetUpload.file?.name || 'Creative',
              primaryAsset: primaryAssetUpload.file!,
              primaryPreview: primaryAssetUpload.preview,
              primaryDimensions: primaryAssetUpload.dimensions,
              primaryAssetError,
              secondaryAsset: secondaryAssetUpload.file,
              secondaryPreview: secondaryAssetUpload.preview,
              secondaryDimensions: secondaryAssetUpload.dimensions,
              destinationAsset: destinationAssetUpload.file,
              destinationPreview: destinationAssetUpload.preview,
              destinationDimensions: destinationAssetUpload.dimensions,
              selectedAdType,
              selectedDestinationType,
              selectedAppId,
              contentTargetValue,
              destinationURL,
              selectedAdslots,
              selectedContentPartner,
              country: selectedCountry,
              osv: selectedOSV || [],
              isValid: isCurrentItemValid,
              validationMessage: isCurrentItemValid ? '' : validationMessage,
              isVideoAutoFilled,
              adslotValidateCheck: adslotValidation.validateCheck,
              adslotValidationStatus: adslotValidation.validationStatus,
              adslotValidateError: adslotValidation.validateError,
              adslotMandatoryFields: adslotValidation.mandatoryFields,
              clickableStreamUrl: clickableStream.streamUrl,
              clickableStreamError: clickableStream.error,
              optionalStreamUrl: optionalStream.streamUrl,
              optionalStreamError: optionalStream.error,
              destinationStreamUrl: destinationStream.streamUrl,
              destinationStreamError: destinationStream.error,
              clickableStreamMetadata: clickableStream.metadata,
              optionalStreamMetadata: optionalStream.metadata,
              destinationStreamMetadata: destinationStream.metadata,
            }
          : item,
      ),
    );
  }, [
    adslotValidation.validateError,
    adslotValidation.validateCheck,
    adslotValidation.validationStatus,
    adslotValidation.mandatoryFields,
    clickableStream.error,
    clickableStream.metadata,
    clickableStream.streamUrl,
    contentTargetValue,
    destinationAssetUpload.dimensions,
    destinationAssetUpload.file,
    destinationAssetUpload.preview,
    destinationStream.error,
    destinationStream.metadata,
    destinationStream.streamUrl,
    destinationURL,
    getCurrentValidationMessage,
    isVideoAutoFilled,
    optionalStream.error,
    optionalStream.metadata,
    optionalStream.streamUrl,
    primaryAssetError,
    primaryAssetUpload.dimensions,
    primaryAssetUpload.file,
    primaryAssetUpload.preview,
    secondaryAssetUpload.dimensions,
    secondaryAssetUpload.file,
    secondaryAssetUpload.preview,
    selectedAdType,
    selectedAdslots,
    selectedAppId,
    selectedBatchItemId,
    selectedContentPartner,
    selectedCountry,
    selectedDestinationType,
    selectedOSV,
    validation.saveStatus,
  ]);

  // ─── Progress view ─────────────────────────────────────────────────────────

  const showProgressView =
    saveWorkflow.progressview ||
    mosaicValidation.progressview ||
    adslotValidation.progressview;
  const progressStatus = saveWorkflow.progressview
    ? saveWorkflow.progressStatus
    : mosaicValidation.progressview
      ? mosaicValidation.progressStatus
      : adslotValidation.progressStatus;
  const invalidBatchItem = batchItems.find(item => !item.isValid);
  const areAllBatchItemsValid =
    batchItems.length > 0 && batchItems.every(item => item.isValid);
  const isSelectedBatchItemCurrentlyValid = selectedBatchItemId
    ? validation.saveStatus &&
      !primaryAssetError &&
      !adslotValidation.validateError
    : true;
  const currentManualGeneratedTestCount =
    selectedAdslots.length * Math.max(selectedOSV?.length || 0, 1);
  const batchGeneratedTestCount =
    batchItems.length > 0
      ? getAdsureGeneratedTestCount(batchItems)
      : currentManualGeneratedTestCount;
  const isExceedingBatchGeneratedTestLimit =
    activeTab === 'manual' &&
    batchGeneratedTestCount > MAX_BATCH_TESTS;
  const footerValidationMessage =
    isExceedingBatchGeneratedTestLimit
      ? getMaxBatchTestsMessage(batchGeneratedTestCount)
      : activeTab === 'manual' && invalidBatchItem
      ? `${invalidBatchItem.label}: ${
          invalidBatchItem.validationMessage ||
          'Invalid test: please correct configuration.'
        }`
      : validation.mandatoryFields;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <S.Container style={{ flex: 1, overflow: 'auto', marginBottom: 0 }}>
        <S.Divider />
        {showProgressView ? (
          <ProgressView status={progressStatus} />
        ) : (
          <>
            <Tabs activeTab={activeTab} onTabChange={setActiveTab} />

            {activeTab === 'manual' && (
              <ManualUploadFormAdsure
                primaryAsset={primaryAssetUpload.file}
                primaryPreview={primaryAssetUpload.preview}
                primaryDimensions={primaryAssetUpload.dimensions}
                secondaryAsset={secondaryAssetUpload.file}
                secondaryPreview={secondaryAssetUpload.preview}
                secondaryDimensions={secondaryAssetUpload.dimensions}
                destinationAsset={destinationAssetUpload.file}
                destinationPreview={destinationAssetUpload.preview}
                destinationDimensions={destinationAssetUpload.dimensions}
                selectedAdType={selectedAdType}
                selectedDestinationType={selectedDestinationType}
                selectedAppId={selectedAppId}
                contentTargetValue={contentTargetValue}
                destinationURL={destinationURL}
                selectedAdslot={selectedAdslot}
                selectedAdslots={selectedAdslots}
                selectedContentPartner={selectedContentPartner}
                country={selectedCountry}
                osv={selectedOSV}
                adslotsData={adslotsData}
                isAdslotFetching={isAdslotFetching}
                isAdslotLoading={isAdslotLoading}
                loadAppOptions={appSearch.loadAppOptions}
                isAppFetching={appSearch.isAppFetching}
                isAppLoading={appSearch.isAppLoading}
                loadContentOptions={contentPartnerSearch.loadContentOptions}
                isContentPartnerFetching={
                  contentPartnerSearch.isContentPartnerFetching
                }
                isContentPartnerLoading={
                  contentPartnerSearch.isContentPartnerLoading
                }
                onPrimaryFileUpload={handlePrimaryFileUpload}
                onPrimaryDrop={handlePrimaryDrop}
                onPrimaryDragOver={primaryAssetUpload.handleDragOver}
                onPrimaryRemove={handlePrimaryRemove}
                onSecondaryFileUpload={secondaryAssetUpload.handleFileUpload}
                onSecondaryDrop={secondaryAssetUpload.handleDrop}
                onSecondaryDragOver={secondaryAssetUpload.handleDragOver}
                onSecondaryRemove={secondaryAssetUpload.handleRemove}
                onDestinationFileUpload={
                  destinationAssetUpload.handleFileUpload
                }
                onDestinationDrop={destinationAssetUpload.handleDrop}
                onDestinationDragOver={destinationAssetUpload.handleDragOver}
                onDestinationRemove={destinationAssetUpload.handleRemove}
                onAdTypeChange={handleAdTypeChange}
                onDestinationTypeChange={handleDestinationTypeChange}
                onAppIdChange={setselectedAppId}
                onContentTargetChange={setContentTargetValue}
                onDestinationURLChange={e => setDestinationURL(e.target.value)}
                onAdslotChange={handleAdslotChange}
                onAdslotsChange={handleAdslotsChange}
                onContentPartnerChange={setContentPartner}
                setIsOpen={setIsOpen}
                isOpen={isOpen}
                adslotValidation={adslotValidation}
                onCountryChange={setCountry}
                onOSVersionChange={setOsv}
                appNameOption={appSearch.appOptions}
                isVideoAutoFilled={isVideoAutoFilled}
                clickableStreamUrl={clickableStream.streamUrl}
                clickableStreamMetadata={clickableStream.metadata}
                clickableStreamError={clickableStream.error}
                clickableStreamLoading={clickableStream.isLoading}
                onClickableStreamUrlChange={clickableStream.setUrl}
                onClickableStreamUrlBlur={() =>
                  clickableStream.validateStream({
                    url: clickableStream.streamUrl,
                    role: 'primary',
                    adType: 'clickableStreamTo',
                    tvServerEnvironment: 'production',
                  })
                }
                onClickableStreamDurationChange={clickableStream.setDuration}
                optionalStreamUrl={optionalStream.streamUrl}
                optionalStreamMetadata={optionalStream.metadata}
                optionalStreamError={optionalStream.error}
                optionalStreamLoading={optionalStream.isLoading}
                onOptionalStreamUrlChange={optionalStream.setUrl}
                onOptionalStreamUrlBlur={() =>
                  optionalStream.validateStream({
                    url: optionalStream.streamUrl,
                    role: 'secondary',
                    adType: 'expandableBannerTo',
                    tvServerEnvironment: 'production',
                  })
                }
                onOptionalStreamDurationChange={optionalStream.setDuration}
                destinationStreamUrl={destinationStream.streamUrl}
                destinationStreamMetadata={destinationStream.metadata}
                destinationStreamError={destinationStream.error}
                destinationStreamLoading={destinationStream.isLoading}
                onDestinationStreamUrlChange={destinationStream.setUrl}
                onDestinationStreamUrlBlur={() =>
                  destinationStream.validateStream({
                    url: destinationStream.streamUrl,
                    role: 'destination',
                    adType: selectedAdType?.value || 'bannerTo',
                    destinationType: 'Stream',
                    tvServerEnvironment: 'production',
                  })
                }
                onDestinationStreamDurationChange={
                  destinationStream.setDuration
                }
                batchItems={batchItems}
                selectedBatchItemId={selectedBatchItemId}
                primaryAssetError={primaryAssetError}
                uploadActionError={uploadActionError}
                canAddCurrentToBatch={
                  validation.saveStatus && !selectedBatchItemId
                }
                isEditingBatchItem={Boolean(selectedBatchItemId)}
                allowMultiplePrimaryUpload
                onAddCurrentToBatch={handleAddCurrentToBatch}
                onSelectBatchItem={handleSelectBatchItem}
                onRemoveBatchItem={handleRemoveBatchItem}
                onAddMoreCreatives={handleAddMoreCreatives}
              />
            )}

            {activeTab === 'mosaic' && (
              <MosaicForm
                campaignID={mosaicValidation.campaignID}
                creativeID={mosaicValidation.creativeID}
                validateError={mosaicValidation.validateError}
                adslotState={mosaicValidation.adslotState}
                selectedAdslot={selectedAdslot}
                creativeAdslots={mosaicValidation.creativeAdslots}
                onCampaignIDChange={mosaicValidation.handleCampaignIDChange}
                onCreativeIDChange={mosaicValidation.handleCreativeIDChange}
                onValidate={mosaicValidation.handleValidateChange}
                onAdslotChange={handleAdslotChange}
                setIsOpen={setIsOpen}
                onOSVersionChange={setOsv}
                selectedOSVersion={selectedOSV}
                osVersionOptions={osVersionSearch.options}
                isOSVFetching={osVersionSearch.isFetching}
                isOSVLoading={osVersionSearch.isLoading}
              />
            )}
            {activeTab === 'webTester' && (
              <WebTesterForm
                destinationURL={destinationURL}
                onDestinationURLChange={e => setDestinationURL(e.target.value)}
                selectedDestinationType={selectedDestinationType}
                onDestinationTypeChange={handleDestinationTypeChange}
                selectedAdslot={{
                  value: 'webTesterAdslot',
                  label: 'webTesterAdslot',
                }}
                setIsOpen={setIsOpen}
                onOSVersionChange={setOsv}
                country={selectedCountry}
                osv={selectedOSV}
                onCountryChange={setCountry}
              />
            )}
          </>
        )}
      </S.Container>
      <ThemeProvider defaultMode="light">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '12px',
            marginBottom: '8px',
            marginRight: '18px',
            marginLeft: '15px',
            fontSize: '14px',
          }}
        >
          <Button
            onClick={() => setOnDemandModal(false)}
            colorVariant="deepGreen"
            renderVariant="borderless"
            variant="borderless"
            disableHoverEffect={true}
          >
            Close
          </Button>
          <span
            style={{
              color: 'red',
              fontSize: 12,
              marginTop: 10,
              fontWeight: 700,
            }}
          >
            {footerValidationMessage}
          </span>
          <Button
            onClick={saveWorkflow.handleSave}
            colorVariant="deepGreen"
            renderVariant="borderless"
            variant="borderless"
            disableHoverEffect={true}
            disabled={
              activeTab === 'manual'
                ? showProgressView ||
                  isExceedingBatchGeneratedTestLimit ||
                  (batchItems.length > 0
                    ? !areAllBatchItemsValid ||
                      !isSelectedBatchItemCurrentlyValid
                    : !validation.saveStatus)
                : !validation.saveStatus || showProgressView
            }
          >
            Save & Test
          </Button>
        </div>
      </ThemeProvider>
    </div>
  );
}
